import React, {useCallback, useMemo} from 'react';
import {createSelector} from 'reselect';

import {useMemoizedSelector} from '../../state/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetActions from '../../state/views/runSet/actions';
import type {Ref as RunSetRef} from '../../state/views/runSet/types';
import * as Query from '../../util/queryts';
import type {GroupSelectionState} from '../../util/selectionmanager';
import {useAddToHistory} from '../PanelBank/PanelBank';
import {type RunSearchHistoryItem} from '../PanelBank/types';
import {ProjectPicker} from '../ProjectPicker/ProjectPicker';
import {RunsSearchBar} from '../RunsSearchBar';
import {useRunSelectorFeatureEnabled} from './RunSelectorFeaturesContext';
import {useRunSelectorProjectInfo} from './RunSelectorRunSetContext';
import {makeRunSetSelector} from './runSetSelectors';
import {type WBTableComp} from './WBTable/WBTable';

type RunSelectorExtraTableActionsTop = {
  enableSetProject: boolean;
  groupSelections: GroupSelectionState;
  runSetRef: RunSetRef;
  setProject: (
    newEntityName: string,
    newProjectName: string,
    newProjectId: string
  ) => void;
  tableRef: React.RefObject<WBTableComp>;
};

const makeRunsSearchSelector = (runSetRef: RunSetRef) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    search: runSet.search,
    searchHistory: runSet.searchHistory,
  }));

export function RunSelectorExtraTableActionsTop(
  props: RunSelectorExtraTableActionsTop
) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const {projectName, entityName, projectInternalId} =
    useRunSelectorProjectInfo();

  const partialRunSet = useMemoizedSelector(
    makeRunsSearchSelector,
    props.runSetRef
  );

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setSearch = useCallback(
    (query: string, isRegex: boolean) => {
      if (props.tableRef.current) {
        props.tableRef.current.resetPagination();
      }
      runSetUpdate({search: {query, isRegex}});
    },
    [props.tableRef, runSetUpdate]
  );

  const setSearchHistory = useCallback(
    (searchHistory: RunSearchHistoryItem[] | undefined) =>
      runSetUpdate({searchHistory}),
    [runSetUpdate]
  );

  const addToHistory = useAddToHistory(
    setSearchHistory,
    partialRunSet.searchHistory
  );

  const search = useMemo(
    () => ({
      query: partialRunSet.search.query,
      isRegex: partialRunSet.search.isRegex ?? true,
    }),
    [partialRunSet.search.query, partialRunSet.search.isRegex]
  );

  return (
    <>
      {props.enableSetProject && projectInternalId && (
        <ProjectPicker
          entityName={entityName}
          projectName={projectName}
          setProject={props.setProject}
          projectId={projectInternalId}
        />
      )}
      {isEnabled('search') ? (
        <div style={{flexGrow: 1}}>
          <RunsSearchBar
            isGroupSearch={Query.isGroupedByGroup(
              props.groupSelections.grouping
            )}
            search={search}
            setSearch={setSearch}
            searchHistory={partialRunSet.searchHistory}
            onAddToHistory={addToHistory}
          />
        </div>
      ) : null}
    </>
  );
}
